import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import logoFigure from "@/assets/logo-arzani-white.webp";
import heroImage from "@/assets/dest-marbella-new.webp";
import destSea from "@/assets/dest-sea.webp";
import marbellaDestinations from "@/assets/marbella-destinations.jpg";
import yachtImage from "@/assets/yacht.webp";
import lifestyleHero from "@/assets/lifestyle-hero.webp";
import residencesHero from "@/assets/residences-hero.webp";
import driveImage from "@/assets/drive.webp";
import villaInterior from "@/assets/villa-interior.webp";
import lifestyleWellness from "@/assets/marbella-tennis.jpg";
import lifestyleRestaurant from "@/assets/marbella-aerial.jpg";
import lifestyleDining from "@/assets/lifestyle-dining-ibiza.webp";

import marbellaPrincess58 from "@/assets/marbella-princess58.webp";
import marbellaCranchiM44 from "@/assets/marbella-cranchi-m44.webp";
import marbellaAnvera48 from "@/assets/marbella-anvera48.webp";
import marbellaCranchiMed50 from "@/assets/marbella-cranchi-med50.webp";

import marbellaHuracan from "@/assets/marbella-huracan-spyder.webp";
import marbellaFerrariF8 from "@/assets/marbella-ferrari-f8.webp";
import marbellaLamboUrus from "@/assets/marbella-lambo-urus.webp";
import marbellaG63 from "@/assets/marbella-g63.webp";
import marbellaPorsche911 from "@/assets/marbella-porsche-911.webp";
import marbellaRRSport from "@/assets/marbella-rr-sport.webp";

import marbellaVillaNoelia from "@/assets/marbella-villa-noelia.webp";
import marbellaVillaVeronique from "@/assets/marbella-villa-veronique.webp";
import marbellaVillaTorremuelle from "@/assets/marbella-villa-torremuelle.webp";
import marbellaVillaSuzanna from "@/assets/marbella-villa-suzanna.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const services = [
  {
    title: "Yachts",
    description: "Private day charters along the Costa del Sol, from Puerto Banús to Gibraltar and beyond.",
    image: yachtImage,
    path: "/yachts/marbella",
  },
  {
    title: "Residences",
    description: "Exceptional villas and apartments across the Golden Mile, Sierra Blanca, and La Zagaleta.",
    image: residencesHero,
  },
  {
    title: "Drive",
    description: "Prestige vehicles delivered to your door — from elegant touring to high-performance driving.",
    image: driveImage,
  },
  {
    title: "Lifestyle",
    description: "Dining reservations, beach clubs, wellness, private chefs, and bespoke experiences arranged with care.",
    image: lifestyleHero,
    path: "/marbella/lifestyle",
  },
];

const marbellaYachts = [
  {
    name: "Cranchi Mediterranée 50",
    price: "From €1.500 / 4hr",
    image: marbellaCranchiMed50,
    specs: ["Length: 15.2 m", "Guests: 12 + 2 Crew", "Captain: Included"],
  },
  {
    name: "Cranchi M44 HT",
    price: "From €1.850 / 4hr",
    image: marbellaCranchiM44,
    specs: ["Length: 13.4 m", "Guests: 10 + Skipper", "Captain: Included"],
  },
  {
    name: "Princess 58",
    price: "From €2.300 / 4hr",
    image: marbellaPrincess58,
    specs: ["Length: 17.7 m", "Guests: 12 + 2 Crew", "Captain: Included"],
  },
  {
    name: "Anvera 48",
    price: "From €3.750 / 8hr",
    image: marbellaAnvera48,
    specs: ["Length: 14.6 m", "Guests: 11 + Skipper", "Captain: Included"],
  },
];

const marbellaCars = [
  {
    name: "Lamborghini Huracán EVO Spyder",
    price: "From €1.500/day",
    image: marbellaHuracan,
    specs: ["Passengers: 2", "Engine: 5.2L V10", "Power: 640 HP"],
  },
  {
    name: "Ferrari F8 Spider",
    price: "From €1.700/day",
    image: marbellaFerrariF8,
    specs: ["Passengers: 2", "Engine: 3.9L V8", "Power: 720 HP"],
  },
  {
    name: "Lamborghini Urus",
    price: "From €1.200/day",
    image: marbellaLamboUrus,
    specs: ["Passengers: 5", "Engine: 4.0L V8 Biturbo", "Power: 660 HP"],
  },
  {
    name: "Mercedes-AMG G63",
    price: "From €800/day",
    image: marbellaG63,
    specs: ["Passengers: 5", "Engine: 4.0L V8 Biturbo", "Power: 585 HP"],
  },
  {
    name: "Porsche 911 Carrera Cabrio",
    price: "From €700/day",
    image: marbellaPorsche911,
    specs: ["Passengers: 4", "Engine: 3.0L Flat-6", "Power: 385 HP"],
  },
  {
    name: "Range Rover Sport",
    price: "From €500/day",
    image: marbellaRRSport,
    specs: ["Passengers: 5", "Engine: 3.0L I6", "Power: 400 HP"],
  },
];

const marbellaResidences = [
  {
    name: "Villa Noelia",
    price: "From €18.000/week",
    image: marbellaVillaNoelia,
    specs: ["Guests: 12", "Bedrooms: 6", "Bathrooms: 6"],
  },
  {
    name: "Villa Veronique",
    price: "From €13.400/week",
    image: marbellaVillaVeronique,
    specs: ["Guests: 10", "Bedrooms: 5", "Bathrooms: 5"],
  },
  {
    name: "Villa Torremuelle",
    price: "From €10.000/week",
    image: marbellaVillaTorremuelle,
    specs: ["Guests: 8", "Bedrooms: 4", "Bathrooms: 4"],
  },
  {
    name: "Villa Suzanna",
    price: "From €14.800/week",
    image: marbellaVillaSuzanna,
    specs: ["Guests: 10", "Bedrooms: 5", "Bathrooms: 6"],
  },
];

const Marbella = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Arzani Group Marbella — Yachts, Villas & Prestige Vehicles"
        description="Curated luxury concierge in Marbella and the Costa del Sol — yachts, villas, prestige cars and bespoke lifestyle."
        path="/marbella"
        jsonLd={{ "@context": "https://schema.org", "@type": "Service", serviceType: "Luxury concierge", areaServed: "Marbella, Spain", provider: { "@type": "Organization", name: "Arzani Group" } }}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative min-h-[100vh] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <video src="/videos/marbella-hero.mov" autoPlay muted loop playsInline preload="metadata" className="w-full h-full object-cover scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground/50 via-foreground/20 to-foreground/60" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-background" />
        </div>
        <div className="relative z-10 text-center px-8 flex flex-col items-center">
          <motion.img
            src={logoFigure}
            alt="Arzani Group"
            className="h-20 md:h-28 mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.8, ease }}
          />
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wider uppercase mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1.8, ease }}
          >
            Arzani Group
          </motion.h1>
          <motion.p
            className="font-label text-primary-foreground/60 tracking-[0.4em] mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1.8, ease }}
          >
            M A R B E L L A
          </motion.p>
          <motion.p
            className="font-accent text-primary-foreground/60 text-sm md:text-base italic max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 1.8, ease }}
          >
            Mediterranean refinement,<br />effortlessly arranged.
          </motion.p>
        </div>
        <motion.div
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1.5, ease }}
        >
          <span className="font-label text-primary-foreground/30 text-[8px]">Scroll</span>
          <div className="w-px h-8 bg-primary-foreground/20" />
        </motion.div>
      </section>

      {/* Editorial Intro — Asymmetric */}
      <section className="py-24 md:py-36 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img
                src={marbellaDestinations}
                alt="Marbella destination"
                className="w-full h-[280px] md:h-[420px] object-cover object-right"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              className="lg:col-span-4 lg:col-start-9 flex flex-col justify-end"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.15, ease }}
            >
              <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
                The Destination
              </span>
              <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                Marbella is defined by a rare balance — between energy and calm,
                between visibility and privacy, between celebration and stillness.
              </p>
              <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                Through Arzani, every element of your stay is arranged in advance,
                allowing you to move through the destination with complete ease.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Centered Statement */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container-luxury max-w-2xl mx-auto text-center">
          <motion.p
            className="font-accent text-foreground/60 text-base md:text-lg lg:text-xl leading-[1.8] italic"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease }}
          >
            From the Golden Mile to the hills of La Zagaleta,
            every moment is considered — and nothing is left to chance.
          </motion.p>
        </div>
      </section>

      {/* Services — Editorial Grid */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-luxury">
          <motion.div
            className="mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
              Our Services
            </span>
            <h2 className="font-display text-2xl md:text-4xl text-foreground tracking-[0.12em] uppercase">
              What We Arrange
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                className="group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1.2, delay: i * 0.08, ease }}
              >
                <div className="overflow-hidden mb-6">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[220px] md:h-[280px] object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-display text-lg md:text-xl text-foreground tracking-[0.12em] uppercase mb-3">
                  Arzani {service.title}
                </h3>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] max-w-sm mb-5">
                  {service.description}
                </p>
                {service.path ? (
                  <Link
                    to={service.path}
                    className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                  >
                    Choose
                  </Link>
                ) : (
                  <a
                    href="https://wa.me/message/2XEKJ5STR2IZJ1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                  >
                    Choose
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Book */}
      <section className="bg-background py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row">
            {[
              {
                title: "Submit Your Request",
                text: "Tell us your plans, preferences, and dates in Marbella.",
              },
              {
                title: "Curated Options",
                text: "We present a tailored selection of residences, yachts, and services.",
              },
              {
                title: "Confirm & Arrange",
                text: "Once confirmed, every detail is secured and prepared in advance.",
              },
              {
                title: "Experience Marbella",
                text: "Arrive and enjoy — everything is already in place.",
              },
            ].map((item, i, arr) => (
              <motion.div
                key={item.title}
                className="flex md:flex-col flex-1 group"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1, ease }}
              >
                {/* Timeline row */}
                <div className="flex flex-col md:flex-row items-center mr-5 md:mr-0 md:mb-6">
                  <div className="w-5 h-5 rounded-full border border-foreground/20 flex items-center justify-center flex-shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground/40" />
                  </div>
                  {i < arr.length - 1 && (
                    <div className="w-px md:w-full h-full md:h-px bg-foreground/10 md:flex-1 min-h-[40px] md:min-h-0 ml-0 md:ml-0" />
                  )}
                  {i === arr.length - 1 && <div className="md:flex-1" />}
                </div>
                {/* Content */}
                <div className="pb-8 md:pb-0 md:pr-8">
                  <span className="font-label text-[10px] text-foreground/25 tracking-[0.2em] block mb-1.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-sm text-foreground tracking-[0.15em] uppercase mb-1.5">
                    {item.title}
                  </h3>
                  <p className="font-body text-muted-foreground text-xs leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            <motion.div
              className="lg:col-span-4 flex flex-col gap-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <div>
                <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                  Whether your stay is centred around a villa in the hills, a
                  penthouse overlooking the sea, or a spontaneous weekend along the
                  coast — Arzani ensures every detail is already in place.
                </p>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                  Private yachts, prestige vehicles, dining reservations, wellness,
                  and more — each service is carefully selected and coordinated
                  through our trusted local network.
                </p>
              </div>
              <img
                src={villaInterior}
                alt="Interior"
                className="w-full h-[160px] md:h-[200px] object-cover"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              className="lg:col-span-7 lg:col-start-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.1, ease }}
            >
              <img
                src={lifestyleRestaurant}
                alt="Marbella dining"
                className="w-full h-[280px] md:h-[400px] object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Block */}
      <section className="py-20 md:py-32 bg-warm-black">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img
                src={lifestyleDining}
                alt="Marbella lifestyle"
                className="w-full h-[180px] md:h-[240px] object-cover"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              className="lg:col-span-7 lg:col-start-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.5, delay: 0.1, ease }}
            >
              <p className="font-accent text-primary-foreground/60 text-sm md:text-base lg:text-lg leading-[1.8] italic">
                Our role is to understand how each element connects — so your time
                in Marbella moves with the effortless rhythm you expect.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Arzani Yachts Marbella — Carousel */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-luxury">
          <motion.div
            className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <div>
              <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
                Our Fleet
              </span>
              <h2 className="font-display text-2xl md:text-4xl text-foreground tracking-[0.12em] uppercase">
                Arzani Yachts Marbella
              </h2>
            </div>
            <Link
              to="/yachts/marbella"
              className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase self-start md:self-auto"
            >
              View full fleet
            </Link>
          </motion.div>

          <Carousel opts={{ align: "start", loop: false }} className="w-full">
            <CarouselContent className="-ml-6 md:-ml-10">
              {marbellaYachts.map((yacht) => (
                <CarouselItem key={yacht.name} className="pl-6 md:pl-10 basis-[85%] md:basis-[45%] lg:basis-[35%]">
                  <div className="group min-w-0">
                    <div className="aspect-[4/3] overflow-hidden mb-6">
                      <img
                        src={yacht.image}
                        alt={yacht.name}
                        className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="font-display text-lg md:text-xl text-foreground mb-2 tracking-[0.12em] uppercase">
                      {yacht.name}
                    </h3>
                    <p className="font-label text-bronze mb-4 text-xs">{yacht.price}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-5">
                      {yacht.specs.map((spec) => (
                        <span key={spec} className="font-body text-muted-foreground text-xs italic">{spec}</span>
                      ))}
                    </div>
                    <Link
                      to="/yachts/marbella"
                      className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                    >
                      Choose
                    </Link>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex items-center gap-4 mt-10">
              <CarouselPrevious className="static translate-y-0 h-10 w-10 border-foreground/20 text-foreground hover:bg-foreground/5" />
              <CarouselNext className="static translate-y-0 h-10 w-10 border-foreground/20 text-foreground hover:bg-foreground/5" />
            </div>
          </Carousel>
        </div>
      </section>

      {/* Arzani Drive Marbella — Carousel */}
      <section className="py-20 md:py-32 bg-background border-t border-foreground/5">
        <div className="container-luxury">
          <motion.div
            className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <div>
              <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
                Our Fleet
              </span>
              <h2 className="font-display text-2xl md:text-4xl text-foreground tracking-[0.12em] uppercase">
                Arzani Drive Marbella
              </h2>
            </div>
            <Link
              to="/drive/marbella"
              className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase self-start md:self-auto"
            >
              View full fleet
            </Link>
          </motion.div>

          <Carousel opts={{ align: "start", loop: false }} className="w-full">
            <CarouselContent className="-ml-6 md:-ml-10">
              {marbellaCars.map((car) => (
                <CarouselItem key={car.name} className="pl-6 md:pl-10 basis-[85%] md:basis-[45%] lg:basis-[35%]">
                  <div className="group min-w-0">
                    <div className="aspect-[4/3] overflow-hidden mb-6 bg-muted/30 flex items-center justify-center">
                      <img
                        src={car.image}
                        alt={car.name}
                        className="w-full h-full object-contain transition-transform duration-[2000ms] ease-out group-hover:scale-[1.03] p-4"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="font-display text-lg md:text-xl text-foreground mb-2 tracking-[0.12em] uppercase">
                      {car.name}
                    </h3>
                    <p className="font-label text-bronze mb-4 text-xs">{car.price}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-5">
                      {car.specs.map((spec) => (
                        <span key={spec} className="font-body text-muted-foreground text-xs italic">{spec}</span>
                      ))}
                    </div>
                    <a
                      href="https://wa.me/message/2XEKJ5STR2IZJ1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                    >
                      Choose
                    </a>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex items-center gap-4 mt-10">
              <CarouselPrevious className="static translate-y-0 h-10 w-10 border-foreground/20 text-foreground hover:bg-foreground/5" />
              <CarouselNext className="static translate-y-0 h-10 w-10 border-foreground/20 text-foreground hover:bg-foreground/5" />
            </div>
          </Carousel>
        </div>
      </section>

      {/* Arzani Residences Marbella — Carousel */}
      <section className="py-20 md:py-32 bg-background border-t border-foreground/5">
        <div className="container-luxury">
          <motion.div
            className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <div>
              <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
                Our Collection
              </span>
              <h2 className="font-display text-2xl md:text-4xl text-foreground tracking-[0.12em] uppercase">
                Arzani Residences Marbella
              </h2>
            </div>
            <Link
              to="/marbella/residences"
              className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase self-start md:self-auto"
            >
              View all residences
            </Link>
          </motion.div>

          <Carousel opts={{ align: "start", loop: false }} className="w-full">
            <CarouselContent className="-ml-6 md:-ml-10">
              {marbellaResidences.map((res) => (
                <CarouselItem key={res.name} className="pl-6 md:pl-10 basis-[85%] md:basis-[45%] lg:basis-[35%]">
                  <div className="group min-w-0">
                    <div className="aspect-[4/3] overflow-hidden mb-6">
                      <img
                        src={res.image}
                        alt={res.name}
                        className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="font-display text-lg md:text-xl text-foreground mb-2 tracking-[0.12em] uppercase">
                      {res.name}
                    </h3>
                    <p className="font-label text-bronze mb-4 text-xs">{res.price}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-5">
                      {res.specs.map((spec) => (
                        <span key={spec} className="font-body text-muted-foreground text-xs italic">{spec}</span>
                      ))}
                    </div>
                    <a
                      href="https://wa.me/message/2XEKJ5STR2IZJ1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                    >
                      Choose
                    </a>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex items-center gap-4 mt-10">
              <CarouselPrevious className="static translate-y-0 h-10 w-10 border-foreground/20 text-foreground hover:bg-foreground/5" />
              <CarouselNext className="static translate-y-0 h-10 w-10 border-foreground/20 text-foreground hover:bg-foreground/5" />
            </div>
          </Carousel>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <motion.div
              className="lg:col-span-4 lg:col-start-2"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img
                src={lifestyleWellness}
                alt="Wellness"
                className="w-full h-[200px] md:h-[260px] object-cover"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              className="lg:col-span-3 lg:col-start-7 lg:mt-20"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.15, ease }}
            >
              <img
                src={destSea}
                alt="Sea"
                className="w-full h-[150px] md:h-[190px] object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>

          <motion.div
            className="max-w-2xl mx-auto text-center mt-14 md:mt-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease }}
          >
            <p className="font-body text-foreground/60 text-xs md:text-sm leading-[2] mb-5">
              From beach clubs to Michelin restaurants, from sunrise wellness to
              late-night dining — every reservation, every transfer, every detail
              is handled before you arrive.
            </p>
            <p className="font-body text-foreground/50 text-xs md:text-sm leading-[2]">
              No waiting. No uncertainty. Just access.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA — Get in Touch */}
      <section className="py-24 md:py-36 bg-warm-black">
        <div className="container-luxury max-w-2xl mx-auto text-center">
          <motion.span
            className="font-label text-bronze block mb-6 text-[10px] tracking-[0.3em]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            Coming Soon
          </motion.span>
          <motion.h2
            className="font-display text-2xl md:text-4xl text-primary-foreground tracking-[0.12em] uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            Marbella, Summer 2025
          </motion.h2>
          <motion.p
            className="font-body text-primary-foreground/50 text-xs md:text-sm leading-[2] mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 1.2, ease }}
          >
            We are preparing our full Marbella offering. Register your interest
            and be the first to access our curated services.
          </motion.p>
          <motion.a
            href="https://wa.me/message/2XEKJ5STR2IZJ1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-3 border border-primary-foreground/30 text-primary-foreground/80 text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-300"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 1, ease }}
          >
            Get in touch
          </motion.a>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Marbella;
